/**
 * `RolloutBackend` over browser Web Workers.
 *
 * ===========================================================================
 * WHAT THIS IS FOR
 * ===========================================================================
 *
 * Training in a tab has exactly one hard constraint: the tab must stay
 * interactive. A rollout is thousands of physics ticks per chunk, and running
 * it on the main thread does not make the page slow, it makes the page STOP,
 * with no frames and no input. So the rollouts go to workers and the main
 * thread keeps only the PPO update.
 *
 * WHAT THAT ACTUALLY BOUGHT, MEASURED. On a 32-core desktop, four workers, a
 * 1,024-step iteration on the default two-by-256 network: the collection costs
 * ~0.15 s and the update costs ~2.9 s. The rollouts are no longer the problem
 * and the update now is — see `defaultTrainingSettings` in ../game/settings.ts
 * for the table, and `TrainingView.updateSeconds` for the instrument that puts
 * it on screen. Moving THIS half off the main thread is the next thing worth
 * doing; it is not what this file does.
 *
 * The workers are the only thing that is new here. Everything they run is
 * `RolloutCollector`, the same class `createSyncBackend` runs in-process and
 * the same one ./rollout-worker runs on a Node thread, and the chunking is
 * `splitSteps` from ./trainer — so chunk k gets the same budget and the same
 * seed under all three, and "the browser backend agrees with the reference" is
 * a claim about one implementation rather than three that are meant to agree.
 *
 * ===========================================================================
 * THE FALLBACK IS NOT OPTIONAL
 * ===========================================================================
 *
 * `Worker` is absent in Node, absent in a few locked-down embeddings, and
 * constructing one can throw outright under a strict Content-Security-Policy.
 * All three land on `createSyncBackend`, which is slower and blocks — but
 * training that stutters is a different thing from training that will not
 * start, and the caller is told which one it got through `parallel`.
 */

import { createSyncBackend, splitSteps, type EpisodeStats, type RolloutBackend, type TrainerConfig } from './trainer';
import {
  unpackTrajectories,
  type BrowserWorkerRequest,
  type BrowserWorkerResponse,
  type PackedTrajectories,
} from './browser-worker';
import type { Transition } from './types';

/** Upper bound on workers. Past the core count they time-slice, and one of the
 *  things they take slices from is the thread drawing the charts. */
export const MAX_BROWSER_WORKERS = 8;

/**
 * Cores worth using, or a conservative guess. `hardwareConcurrency` is absent
 * in Node and reported as a rounded-down lie by Safari, so it is a hint.
 */
export const suggestedWorkerCount = (): number => {
  const cores =
    typeof navigator !== 'undefined' && typeof navigator.hardwareConcurrency === 'number'
      ? navigator.hardwareConcurrency
      : 4;
  // One core is left for the main thread: it renders the menu, the charts and —
  // when watch-live is on — a whole match.
  return Math.min(MAX_BROWSER_WORKERS, Math.max(1, Math.floor(cores) - 1));
};

/** True when this environment can actually construct a module worker. */
export const workersAvailable = (): boolean =>
  typeof Worker !== 'undefined' && typeof URL !== 'undefined';

export interface BrowserBackend extends RolloutBackend {
  /** False when the fallback is in use, so the UI can say so rather than
   *  quietly being slow. */
  readonly parallel: boolean;
  /** How many chunks the work is split into, workers or not. */
  readonly chunks: number;
  /**
   * Called when a collection begins and again when it ends.
   *
   * An EDGE, not a duration, because nothing under src/rl may read a wall clock
   * — test/sim/decoupling.test.ts enforces that, and the browser adapters are
   * exempted from the DOM rule and from nothing else. The caller owns the clock
   * and therefore owns the measurement; this only says when to take it. What
   * the UI does with it is show the split between the half of an iteration that
   * runs in workers and the half that blocks the main thread, which is
   * otherwise invisible: steps/second looks identical either way.
   */
  onCollectSpan?: (edge: 'start' | 'end') => void;
}

/** Merge, so the trainer sees one `EpisodeStats` per iteration however many
 *  chunks produced it. Same arithmetic as ./trainer's private version; kept
 *  here rather than exported from there because summing six counters is not a
 *  thing worth coupling two modules over. */
const mergeInto = (into: EpisodeStats, from: EpisodeStats): void => {
  into.episodes += from.episodes;
  into.returnSum += from.returnSum;
  into.lengthTickSum += from.lengthTickSum;
  into.goalsFor += from.goalsFor;
  into.goalsAgainst += from.goalsAgainst;
  into.touchedEpisodes += from.touchedEpisodes;
  // The curriculum gate's numerator and denominator. Omitting them here would
  // pin a browser run's difficulty at its start exactly as the per-agent metric
  // used to pin the CLI's — see `CurriculumConfig` in ./trainer.
  into.envEpisodes += from.envEpisodes ?? 0;
  into.envTouchedEpisodes += from.envTouchedEpisodes ?? 0;
  for (const [name, value] of Object.entries(from.rewardTermSum)) {
    into.rewardTermSum[name] = (into.rewardTermSum[name] ?? 0) + value;
  }
};

const zeroStats = (): EpisodeStats => ({
  episodes: 0,
  returnSum: 0,
  lengthTickSum: 0,
  goalsFor: 0,
  goalsAgainst: 0,
  touchedEpisodes: 0,
  envEpisodes: 0,
  envTouchedEpisodes: 0,
  rewardTermSum: {},
});

/**
 * One worker and the request it is currently answering.
 *
 * The pending resolver is held here rather than in a map keyed by request id
 * because a worker answers exactly one request at a time: the backend fans a
 * single collection out over all of them and awaits the lot.
 */
interface Slot {
  worker: Worker;
  index: number;
  pending: ((response: BrowserWorkerResponse) => void) | null;
}

/**
 * The worker pool.
 *
 * `new Worker(new URL('./browser-worker.ts', import.meta.url), { type: 'module' })`
 * is the form Vite understands: it is recognised statically, the worker is
 * emitted as its own chunk, and the URL survives hashing in a production
 * build. A string path would work in dev and 404 after `vite build`.
 */
const spawn = (config: TrainerConfig, chunks: number): Slot[] => {
  const slots: Slot[] = [];
  for (let index = 0; index < chunks; index++) {
    const worker = new Worker(new URL('./browser-worker.ts', import.meta.url), {
      type: 'module',
    });
    const slot: Slot = { worker, index, pending: null };
    worker.addEventListener('message', event => {
      const response = event.data as BrowserWorkerResponse;
      // `ready` is unsolicited: the worker sends it when its environment has
      // been built, which can land AFTER the first collection has been posted.
      // Letting it resolve the pending request is how the first iteration of a
      // run reports "unexpected reply" and dies.
      if (response.kind === 'ready') return;
      const resolve = slot.pending;
      slot.pending = null;
      resolve?.(response);
    });
    worker.addEventListener('error', event => {
      const resolve = slot.pending;
      slot.pending = null;
      // A worker that fails to LOAD never sends a message, so without this the
      // first collection would hang instead of reporting a broken build.
      resolve?.({ kind: 'error', id: -1, message: event.message || 'worker failed to load' });
    });
    const init: BrowserWorkerRequest = { kind: 'init', init: { config, index } };
    worker.postMessage(init);
    slots.push(slot);
  }
  return slots;
};

const ask = (slot: Slot, request: BrowserWorkerRequest): Promise<BrowserWorkerResponse> =>
  new Promise(resolve => {
    slot.pending = resolve;
    // `params` is cloned rather than transferred: every worker needs its own
    // copy of the same vector, and a transfer would detach it after the first.
    slot.worker.postMessage(request);
  });

/**
 * A backend that collects in Web Workers, falling back to the synchronous one
 * where they are unavailable.
 *
 * Workers are spawned EAGERLY, at construction, not on the first collection:
 * building an `RLEnv` means building the soccar mesh and its BVH, and paying
 * for that four times over inside the first `collect` is the one place where
 * the delay is indistinguishable from the trainer having hung.
 */
export const createBrowserBackend = (
  config: TrainerConfig,
  workers = config.workers
): BrowserBackend => {
  const chunks = Math.max(1, Math.min(MAX_BROWSER_WORKERS, Math.floor(workers)));

  if (!workersAvailable()) {
    const inner = createSyncBackend(config, chunks);
    const fallback: BrowserBackend = {
      parallel: false,
      chunks,
      async collect(
        params: Float64Array,
        steps: number,
        seed: number,
        difficulty?: number
      ): Promise<Transition[][]> {
        fallback.onCollectSpan?.('start');
        try {
          return await inner.collect(params, steps, seed, difficulty);
        } finally {
          fallback.onCollectSpan?.('end');
        }
      },
      episodeStats: () => inner.episodeStats?.() ?? null,
      setOpponent: (params: Float64Array | null) => inner.setOpponent?.(params),
      dispose: () => inner.dispose(),
      get onProgress() {
        return inner.onProgress;
      },
      set onProgress(fn) {
        inner.onProgress = fn;
      },
    };
    return fallback;
  }

  const slots = spawn(config, chunks);
  let stats: EpisodeStats | null = null;
  let opponent: Float64Array | null = null;
  let requestId = 0;
  let disposed = false;

  const backend: BrowserBackend = {
    parallel: true,
    chunks,

    async collect(
      params: Float64Array,
      steps: number,
      seed: number,
      difficulty?: number
    ): Promise<Transition[][]> {
      if (disposed) throw new Error('collect on a disposed backend');
      backend.onCollectSpan?.('start');
      const budgets = splitSteps(steps, slots.length);
      const id = ++requestId;

      // Progress is reported as chunks land rather than continuously: a worker
      // cannot report mid-collection without a second channel, and a bar that
      // moves in four steps is honest where one that interpolates is not.
      let done = 0;
      const replies = slots.map((slot, i) =>
        budgets[i] <= 0
          ? Promise.resolve<BrowserWorkerResponse>({
              kind: 'batch',
              id,
              packed: emptyPacked(),
              stats: zeroStats(),
            })
          : ask(slot, {
              kind: 'collect',
              id,
              params,
              steps: budgets[i],
              seed,
              opponent,
              ...(difficulty === undefined ? {} : { difficulty }),
            }).then(response => {
              done += budgets[i];
              backend.onProgress?.(done);
              return response;
            })
      );

      const settled = await Promise.all(replies);
      backend.onCollectSpan?.('end');
      const merged = zeroStats();
      const out: Transition[][] = [];
      // Concatenated in CHUNK order, exactly as `createSyncBackend` does, so
      // the batch a given seed produces does not depend on which worker
      // happened to finish first.
      for (const response of settled) {
        if (response.kind === 'error') throw new Error(`rollout worker: ${response.message}`);
        if (response.kind !== 'batch') throw new Error('rollout worker: unexpected reply');
        out.push(...unpackTrajectories(response.packed));
        mergeInto(merged, response.stats);
      }
      stats = merged;
      return out;
    },

    episodeStats: () => stats,

    setOpponent(params) {
      opponent = params === null ? null : Float64Array.from(params);
    },

    dispose() {
      disposed = true;
      for (const slot of slots) {
        slot.pending?.({ kind: 'error', id: -1, message: 'backend disposed' });
        slot.pending = null;
        slot.worker.terminate();
      }
    },
  };
  return backend;
};

/** A packed batch with nothing in it, for a chunk that was given no budget. */
const emptyPacked = (): PackedTrajectories => ({
  obs: new Float32Array(0),
  actions: new Int32Array(0),
  logProbs: new Float64Array(0),
  rewards: new Float64Array(0),
  values: new Float64Array(0),
  dones: new Uint8Array(0),
  bootstraps: new Float64Array(0),
  lengths: new Int32Array(0),
});
